// @flow
import React from "react";
import withStyles from "elevate-ui/withStyles";
import Typography from "elevate-ui/Typography";
import Button from "elevate-ui/Button";

type $Props = {
  classes: Object,
  isTeam: boolean,
  isOffice: boolean,
};

const Buying = ({ classes, isTeam, isOffice }: $Props) => (
  <div className={classes.root}>
    <Typography type="body">
      Buying a new home is exciting, but it can also be overwhelming. Here's a
      list of tips to help make your home buying experience more pleasant.
    </Typography>
    <img
      className={classes.inlineImage}
      src="https://storage.googleapis.com/elevate-images/elevate-tips-buying.jpg"
      align="right"
      alt="Keys being handed over to new owners"
    />
    <Typography className={classes.subHeading} type="heading4">
      Choose the right person
    </Typography>
    <Typography type="body">
      If you're thinking about buying a home, you'll want to carefully choose
      the real estate professional you work with during the process.
    </Typography>
    <Typography type="body">
      You should commit yourself to working with one sales associate who can
      learn your likes and dislikes in homes to make your home-buying process
      easier. Choose a professional who specializes in residential real estate
      and who has specific knowledge of the local real estate and mortgage
      markets.
    </Typography>
    <Typography type="body">
      The person you choose should listen to you and be interested enough in you
      to find out about your housing needs and preferences. Service first should
      be the motto of the professional you choose with services going above and
      beyond what you expect and need. Doing some preliminary planning before
      you begin your home search will make the entire process more manageable
      and less overwhelming. As part of your initial game plan, you should:
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Check your credit rating
    </Typography>
    <Typography type="body">
      Even if you're sure you have excellent credit, it's wise to double-check
      at the outset. Straightening out any errors or disputed items now will
      avoid troublesome holdups down the road when you're waiting for mortgage
      approval.
    </Typography>
    <Typography type="body">
      You may see disputed items, in addition to errors caused by a faulty
      social security number, a name similar to yours, or a court ordered
      judgment paid off that hasn't been cleared from the public records. If
      such items appear, write a letter to the appropriate credit bureau. Credit
      bureaus are required to help you straighten things out in a reasonable
      time (usually 30 days).
    </Typography>
    <ul>
      <li>
        <strong>TIP:</strong> Make sure that any outdated derogatory entries are
        deleted from your credit file. Adverse credit information is not
        supposed to be reported or included on your credit report after seven
        years (except bankruptcy information, which can be reported up to 10
        years).
      </li>
      <li>
        <strong>TIP:</strong> Officially cancel inactive credit cards. If you
        have an inactive credit card with a $5,000 limit, even though you owe
        nothing on it, some mortgage lenders will consider that a potential
        future debt. Too many inactive credit cards with significant credit
        limits could keep you from obtaining a mortgage loan. Don't just cut up
        your extra cards; officially cancel them, and do it now so there will be
        time for the news to reach the credit bureaus.
      </li>
      <li>
        <strong>TIP:</strong> Hold off on making any major credit card or car
        purchases while you're waiting to apply for a mortgage. Monthly payments
        you're obligated to pay will be counted against you, and reduce the
        amount of the mortgage loan you'll be offered. Even if you've been
        pre-approved for a mortgage, that approval is subject to last-minute
        evaluation of your financial situation, and a spending spree for
        appliances, furniture and other goodies intended for your new home may
        wreck your chances for buying it.
      </li>
    </ul>
    <Typography className={classes.subHeading} type="heading4">
      Pre-qualification and pre-approval on a mortgage
    </Typography>
    <Typography type="body">
      A real estate professional can help "pre-qualify" you for a mortgage
      before you start house-hunting. This process includes analyzing your
      income, assets and present debt to estimate what you may be able to afford
      on a house purchase. Mortgage brokers, or a lender's own mortgage
      counselors can also calculate the same sort of informal estimate for you.
    </Typography>
    <Typography type="body">
      Obtaining mortgage "pre-approval" is another thing entirely. It means that
      you have in hand a lender's written commitment to put together a loan for
      you (subject only to the particular house you want to buy passing the
      lender's appraisal).
    </Typography>
    <Typography type="body">
      Pre-approval makes you a strong buyer, welcomed by sellers. With most
      other purchasers, sellers must tie the house up on a contract while
      waiting to see if the would-be buyer can really obtain financing.
    </Typography>
    <Typography type="body">
      The down side is that you may pay application fees to cover the lender's
      paperwork in verifying your employment, income, assets, debts and credit
      rating. If you later decide not to use that particular lender, you'd have
      to start all over again elsewhere - with no rebate.
    </Typography>
    <Typography type="body">
      Pre-approval will also speed up the entire mortgage procedure once you've
      found the house you want. The only remaining question will be whether the
      house will "appraise" for enough to warrant the loan.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Become an educated buyer
    </Typography>
    <ul>
      <li>
        The web is one of the best ways to search for homes today. With this
        website, you can receive daily emails with new and updated listings from
        the towns and price range of your choice.
      </li>
      <li>
        Search the entire MLS for all homes, condos, land, multi family,
        commercial properties, and past solds at your convenience.
      </li>
      <li>
        View full listing sheets showing amenities, taxes, lot sizes, beds,
        baths, rooms, siding, fireplaces, garages, room sizes and much more.
      </li>
      <li>
        Get property address and see where the properties are located on
        MapQuest.
      </li>
      <li>Check schools and community profiles of your preferred towns.</li>
      <li>Save preferred listings in your own file to view anytime.</li>
      <li>Calculate approximate mortgage payments for specific properties.</li>
      <li>
        <a href="/signup">Click here to sign up now</a>
      </li>
    </ul>
    <Typography className={classes.subHeading} type="heading4">
      Home Inspection
    </Typography>
    <Typography type="body">
      Once you have made an offer on a home, you will need to schedule a home
      inspection, conducted by an independent authorized inspector. It is
      extremely important to hire a reputable inspector so that you know exactly
      what you are buying. Do not hesitate to ask friends, family, and
      co-workers for advice. If you are satisfied with the results of the
      inspection, then you can proceed to the Purchase and Sales agreement. If
      the inspector finds problems with the property, you may want to negotiate
      with the seller to lower the price, or to pay for certain repairs.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Appraisal
    </Typography>
    <Typography type="body">
      Your lender may require you to get an appraisal of the house you want to
      buy, to make sure it is worth the money that you are borrowing. You may
      select your own appraiser, or you may ask your real estate broker to help
      you with this task.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Homeowner's Insurance
    </Typography>
    <Typography type="body">
      Lenders require that you have homeowners insurance, to protect both your
      interests and theirs. Like everything else, be sure to shop around for
      insurance that fits your needs.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Settlement or Closing
    </Typography>
    <Typography type="body">
      Finally, you are ready for the closing. Be sure to read everything before
      you sign! You should have both your real estate broker and an attorney
      present at the closing to ensure that all is in order.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Finally make sure before you buy
    </Typography>
    <Typography type="body">
      Making sure you end up with the right home involves figuring out exactly
      what features you need, want and don't want in a home. Before starting
      your search, you should make a "wish list" to decide which features are
      absolutely essential, which are nice "extras" if you happen to find them,
      and which are completely undesirable.
    </Typography>
    <Typography type="body">
      The more specific you can be about what you're looking for from the
      outset, the more effective your home search will be. Also keep in mind,
      that in the end, every home purchase is a compromise.
    </Typography>
    <Typography type="body">
      Create your own personalized "wish list" and when you're finished filling
      it out, share it with your real estate agent.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      To {isOffice || isTeam ? `contact us` : `contact me`}, click the button
      below!
    </Typography>
    <Button
      element="a"
      href="/contact"
      title={isOffice || isTeam ? `Contact us` : `Contact me`}
      color="primary"
      innerClassName={classes.btnText}
      className={classes.button}
    >
      {isOffice || isTeam ? `Contact us` : `Contact me`}
    </Button>
  </div>
);

const styles = (theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    margin: "20px auto",
    maxWidth: "660px",
    "& strong": {
      fontWeight: "bold",
    },
    "& p": {
      color: theme.colors.gray["700"],
      lineHeight: "1.6",
      marginBottom: theme.spacing.unit * 1.5,
      fontSize: "18px",

      [theme.breakpoints(800)]: {
        fontSize: "20px",
      },
    },
    "& ul": {
      listStyleType: "disc",
      lineHeight: "1.6",
      color: theme.colors.gray["700"],
      margin: "20px 14px 20px 40px",
      fontSize: "18px",

      [theme.breakpoints(800)]: {
        fontSize: "20px",
      },

      "& li": {
        marginBottom: theme.spacing.unit * 1.5,
      },
    },
  },
  subHeading: {
    marginTop: theme.spacing.unit,
    marginBottom: theme.spacing.unit * 0.5,
    fontSize: "21px",

    [theme.breakpoints(800)]: {
      fontSize: "24px",
    },
  },
  inlineImage: {
    width: "100%",
    marginBottom: theme.spacing.unit * 1.5,
    display: "block",
    maxWidth: "100%",
    height: "auto",
    verticalAlign: "middle",
  },
});

export default withStyles(styles)(Buying);
